import { useState, type FormEvent } from 'react';

export default function NewsletterCTA() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setStatus('success');
      setMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }

    if (!email.trim()) return;

    setStatus('loading');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email.trim());

      const res = await fetch('https://readdy.ai/api/form/d90jaagbhec1he3opk1g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setMessage('Thank you for subscribing! Check your inbox for updates.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight">
            Join 10,000+ Buyers
          </h2>
          <p className="mt-3 text-foreground-600 text-sm md:text-base">
            Subscribe to receive the latest Vigorbuy Spreadsheet updates, new seller alerts, and exclusive buying tips straight to your inbox.
          </p>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
          >
            <input
              type="text"
              name="website_alt"
              className="form-honeypot"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <div className="relative flex-1 w-full">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (status === 'success' || status === 'error') setStatus('idle');
                }}
                placeholder="Enter your email address"
                required
                className="w-full px-5 py-3.5 bg-background-100 border border-background-200 rounded-full text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all duration-200"
              />
            </div>
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full sm:w-auto inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
            >
              {status === 'loading' ? (
                <span className="flex items-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Subscribing...
                </span>
              ) : (
                <>
                  Subscribe Now
                  <i className="ri-arrow-right-line"></i>
                </>
              )}
            </button>
          </form>

          {status === 'success' && (
            <p className="mt-4 text-sm text-primary-600 font-medium">{message}</p>
          )}
          {status === 'error' && (
            <p className="mt-4 text-sm text-red-500 font-medium">{message}</p>
          )}

          <p className="mt-5 text-xs text-foreground-400">
            No spam, unsubscribe anytime. We respect your privacy.
          </p>
        </div>
      </div>
    </section>
  );
}